import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import { ArrowRight, CheckCircle, Phone, Star, Award, Shield, Clock, TrendingUp, ChevronRight, Home as HomeIcon } from 'lucide-react';
import { companyInfo, services, testimonials, projects } from '../data/mock';

const HomeBrand = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroImages = [
    projects[6].images[4],
    projects[5].images[4],
    projects[3].images[3],
    projects[0].images[3]
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  return (
    <div className="min-h-screen">
      {/* Hero Section with Carousel */}
      <section className="relative h-[90vh] overflow-hidden">
        {/* Background Carousel */}
        <div className="absolute inset-0">
          {heroImages.map((img, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={img}
                alt={`Projet ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-cyan-900/60"></div>
            </div>
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-3xl">
              <div className="animate-fade-in-up">
                <div className="inline-flex items-center bg-cyan-600 text-white px-6 py-3 rounded-full text-sm font-bold mb-6 shadow-xl">
                  <HomeIcon className="w-5 h-5 mr-2" />
                  LES FAÇADIERS HDF
                </div>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6 animate-fade-in-up" style={{animationDelay: '0.2s'}}>
                Redonnez vie à vos <span className="text-cyan-400">façades</span>
              </h1>
              <p className="text-2xl md:text-3xl text-cyan-100 mb-4 font-semibold animate-fade-in-up" style={{animationDelay: '0.4s'}}>
                {companyInfo.tagline}
              </p>
              <p className="text-xl text-gray-300 mb-8 animate-fade-in-up" style={{animationDelay: '0.5s'}}>
                Expert façadier dans les Hauts-de-France depuis plus de {companyInfo.experience} ans
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up" style={{animationDelay: '0.6s'}}>
                <Link to="/contact">
                  <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white text-lg px-8 py-6 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all">
                    <Phone className="w-6 h-6 mr-2" />
                    Devis gratuit
                  </Button>
                </Link>
                <Link to="/realisations">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-cyan-600 text-lg px-8 py-6 backdrop-blur-sm bg-white/10">
                    Nos réalisations
                    <ArrowRight className="w-6 h-6 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-cyan-500 w-8' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-gradient-to-r from-cyan-600 via-cyan-500 to-teal-600 py-12 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center text-white">
            <div className="transform hover:scale-110 transition-all">
              <div className="text-5xl font-bold mb-2">{companyInfo.experience}</div>
              <div className="text-cyan-100">Ans d'expérience</div>
            </div>
            <div className="transform hover:scale-110 transition-all">
              <div className="text-5xl font-bold mb-2">500+</div>
              <div className="text-cyan-100">Projets réalisés</div>
            </div>
            <div className="transform hover:scale-110 transition-all">
              <div className="text-5xl font-bold mb-2">100%</div>
              <div className="text-cyan-100">Clients satisfaits</div>
            </div>
            <div className="transform hover:scale-110 transition-all">
              <div className="text-5xl font-bold mb-2">24h</div>
              <div className="text-cyan-100">Délai de réponse</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section with Animation */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-600 font-semibold text-lg">NOS EXPERTISES</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 mt-2">
              Services de <span className="text-cyan-600">façadier expert</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Des solutions complètes pour tous vos besoins en façade
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card 
                key={service.id} 
                className="group hover:shadow-2xl transition-all duration-500 border-2 border-gray-200 hover:border-cyan-500 overflow-hidden transform hover:-translate-y-3"
                style={{animationDelay: `${index * 0.1}s`}}
              >
                <CardContent className="p-8 space-y-4 relative">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-100 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-all duration-500"></div>
                  <div className="relative">
                    <div className="bg-gradient-to-br from-cyan-500 to-teal-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:rotate-12 transition-all">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-cyan-600 transition-colors">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <Link to="/services" className="inline-flex items-center text-cyan-600 hover:text-cyan-700 font-semibold group">
                      En savoir plus
                      <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-2 transition-transform" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Gallery Carousel */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-600 font-semibold text-lg">NOTRE TRAVAIL</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 mt-2">
              Découvrez nos <span className="text-cyan-600">réalisations</span>
            </h2>
            <p className="text-xl text-gray-600">
              Des transformations spectaculaires à travers les Hauts-de-France
            </p>
          </div>
          
          <Carousel className="w-full max-w-6xl mx-auto">
            <CarouselContent>
              {projects.map((project) => (
                <CarouselItem key={project.id} className="md:basis-1/2 lg:basis-1/3">
                  <Card className="group overflow-hidden hover:shadow-2xl transition-all duration-500 border-2 border-gray-200 hover:border-cyan-500">
                    <CardContent className="p-0">
                      <div className="relative overflow-hidden">
                        <img
                          src={project.images[project.images.length - 1]}
                          alt={project.title}
                          className="w-full h-72 object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-end p-6">
                          <div className="text-white">
                            <div className="text-sm font-semibold text-cyan-400 mb-1">{project.location}</div>
                            <div className="text-lg font-bold">{project.type}</div>
                          </div>
                        </div>
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                        <p className="text-gray-600 text-sm mb-4">{project.type}</p>
                        <Link to="/realisations" className="text-cyan-600 hover:text-cyan-700 font-semibold flex items-center">
                          Voir le projet
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="bg-cyan-600 text-white hover:bg-cyan-700 border-none" />
            <CarouselNext className="bg-cyan-600 text-white hover:bg-cyan-700 border-none" />
          </Carousel>

          <div className="text-center mt-12">
            <Link to="/realisations">
              <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-6 text-lg shadow-lg">
                Voir toutes nos réalisations
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-500 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="text-cyan-400 font-semibold text-lg">POURQUOI NOUS ?</span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 mt-2">
              Votre partenaire de confiance
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Award, title: "Expertise reconnue", desc: "Plus de 10 ans d'expérience" },
              { icon: Shield, title: "Garantie qualité", desc: "Travaux garantis et assurés" },
              { icon: Clock, title: "Respect des délais", desc: "Planning précis et respecté" },
              { icon: TrendingUp, title: "Meilleurs matériaux", desc: "Produits professionnels" }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="bg-gradient-to-br from-cyan-500 to-teal-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl group-hover:scale-110 group-hover:rotate-6 transition-all">
                  <item.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-600 font-semibold text-lg">TÉMOIGNAGES</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 mt-2">
              Ils nous font <span className="text-cyan-600">confiance</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={testimonial.id} className="border-2 border-gray-200 hover:border-cyan-500 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                <CardContent className="p-8 space-y-4">
                  <div className="flex space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-6 h-6 text-cyan-500 fill-cyan-500" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic text-lg leading-relaxed">"{testimonial.text}"</p>
                  <div className="pt-4 border-t border-gray-200">
                    <div className="font-bold text-gray-900 text-lg">{testimonial.name}</div>
                    <div className="text-cyan-600 font-medium">{testimonial.source}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-cyan-600 via-cyan-500 to-teal-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Prêt à transformer votre façade ?
          </h2>
          <p className="text-xl md:text-2xl text-cyan-50 mb-10">
            Obtenez un devis gratuit et sans engagement en moins de 24h
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-100 text-xl px-12 py-8 shadow-2xl transform hover:scale-110 transition-all">
                <Phone className="w-6 h-6 mr-3" />
                Demander un devis
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-cyan-600 text-xl px-12 py-8 backdrop-blur-sm bg-white/10">
                Appelez-nous maintenant
              </Button>
            </a>
          </div>
          <p className="text-white text-lg mt-6 font-semibold">
            {companyInfo.phone}
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomeBrand;